import { SelectChangeEvent } from "@mui/material";
import React, { ChangeEvent, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { Outlet } from "react-router-dom";
import rehypeRaw from "rehype-raw";

import { RefactorRegularModal } from "../../../components/dialog";
import { usePopupContext } from "../../../contexts/popupProvider";
import { ILang } from "../../langs/types";
import useTermsOfService from "../hooks/useTermsOfService";
import styles from "./TermsOfServiceModal.module.scss";

const languages: ILang[] = [
    { value: "en-US", label: "LANGUAGE.ENUS" },
    { value: "zh-TW", label: "LANGUAGE.ZHTW" },
    { value: "ja-JP", label: "LANGUAGE.JAJP" },
];
const TermsOfServiceModal = () => {
    const { t } = useTranslation();

    const { Container, Title, Actions, Confirm } = RefactorRegularModal;
    const [isChecked, setIsChecked] = useState(false);
    const [canCheck, setCanCheck] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    const { popups } = usePopupContext();
    const { content, handleConfirm, handleLangChange, selectedLang } = useTermsOfService();

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        // 判斷是否卷軸到底
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 2) {
            setCanCheck(true);
        }
    }, []);

    const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
        setIsChecked(e.target.checked);
    };

    if (!content) return null;

    // Wrapper function for select change event to match hook signature
    const handleLangChangeWrapper = (e: React.ChangeEvent<HTMLSelectElement>) => {
        handleLangChange({ target: { value: e.target.value } } as SelectChangeEvent);
    };

    const rawContentData = content?.data;

    return (
        <>
            <RefactorRegularModal>
                <Container
                    props={{
                        open: popups?.terms_of_service || false,
                        onClose: (_, reason) => {
                            if (reason === "backdropClick") {
                                return;
                            }
                        },
                    }}
                >
                    <div className={styles.langSelectWrapper}>
                        <select
                            className={styles.langSelect}
                            value={selectedLang}
                            onChange={handleLangChangeWrapper}
                            aria-label='選擇語言'
                        >
                            {languages.map((lang) => (
                                <option key={lang.value} value={lang.value}>
                                    {t(lang.label)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <p className={styles.lastUpdated}>
                        {t("COMMON.Last_Updated_DateTime")}：
                        {new Intl.DateTimeFormat("zh-TW", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                        }).format(new Date(content.lastUpdated * 1000))}
                    </p>

                    <div className={styles.modalContent} ref={scrollRef} onScroll={handleScroll}>
                        <Title classes={{ title: styles.modalTitle }}>{t("COMMON.Terms_And_Policy")}</Title>
                        {Object?.keys(rawContentData).map((key) => {
                            const { title, rawContent } = rawContentData[key as keyof typeof rawContentData];
                            return (
                                <React.Fragment key={key}>
                                    <div className={styles.contentTitle}>{title}</div>

                                    <div className={styles.contentSection} tabIndex={0} aria-label='服務條款內容'>
                                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>{rawContent}</ReactMarkdown>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                        <div className={styles.agreementNotice}>
                            {!canCheck && (
                                <span className={styles.scrollHint} role='alert'>
                                    {t("COMMON.Read_All_Term")}
                                </span>
                            )}
                            <div className={styles.checkboxRow}>
                                <input
                                    type='checkbox'
                                    id='tos-agree'
                                    checked={isChecked}
                                    onChange={handleCheckboxChange}
                                    disabled={!canCheck}
                                    aria-label='我已閱讀並同意服務條款'
                                />
                                <label htmlFor='tos-agree' style={{ marginLeft: 8, userSelect: "none" }}>
                                    {t("COMMON.Terms_Agree_Read_Content")}
                                </label>
                            </div>
                        </div>
                        <div className={styles.actions}>
                            <Actions>
                                <Confirm
                                    text={t("COMMON.Terms_Agree") || "同意並繼續使用"}
                                    onClick={handleConfirm}
                                    color='primary'
                                    aria-label='同意並繼續使用'
                                    disabled={!isChecked}
                                />
                            </Actions>
                        </div>
                    </div>
                </Container>
            </RefactorRegularModal>
            <Outlet />
        </>
    );
};
export default TermsOfServiceModal;
